import { createZodFormResolver, lt, useFeatureFlag } from '@mews/framework';
import {
    CommanderFormProps,
    SchemaFormDefinition,
    SchemaFormFields,
    useDialogForm,
    useHandledDialogFormControls,
} from '@mews-commander/form';
import { createOption } from '@mews-commander/ui';
import { PayoutReportExportUpdateParameters, useInvalidateRepositoryQuery } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { useGetExportTargets, useGetScheduleExport, useUpdateScheduleExportPayoutReport } from '../../queries';
import {
    getScheduleConfigFormDefinition,
    exportConfigFormDefinition,
    combinedSchema,
    CombinedSchemaType,
} from './schema';

export interface WithExportScheduleProps {
    exportScheduleId: string;
    refetchTableData: () => void;
}

export const usePayoutReportEditScheduledExportDialogForm = (exportScheduleProps: WithExportScheduleProps) =>
    useDialogForm({
        id: 'payoutReportEditScheduledExportForm',
        renderForm: (props) => <PayoutReportEditScheduledExportForm {...exportScheduleProps} {...props} />,
        title: lt('EditScheduleConfiguration'),
        saveLabel: lt('PayoutExportSaveLabel'),
    });

const PayoutReportEditScheduledExportForm = ({
    exportScheduleId,
    refetchTableData,
    ...props
}: CommanderFormProps & WithExportScheduleProps) => {
    const enablePayoutDetailsInPayoutExport: boolean = useFeatureFlag(FeatureFlag.enablePayoutDetailsInPayoutExport);
    const { data: exportTargets } = useGetExportTargets();
    const scheduledExport = useGetScheduleExport(exportScheduleId);
    const { mutateAsync: updateScheduledExport } = useUpdateScheduleExportPayoutReport();
    const invalidate = useInvalidateRepositoryQuery();

    const exportTargetsOptions =
        exportTargets?.map((target) =>
            createOption({
                label: target.name,
                value: target.id,
            })
        ) ?? [];

    const { renderFormContent } = useHandledDialogFormControls({
        ...props,
        onSubmit: async (data: CombinedSchemaType) => {
            const requestObject: PayoutReportExportUpdateParameters = {
                exportScheduleId,
                name: data.name,
                exportTargetId: data.exportTargetId,
                format: data.format,
                frequency: data.frequency,
                isEnabled: !data.isEnabled,
                notifyCreatorAboutExport: data.notifyCreatorAboutExport,
                reportInterval: data.reportInterval,
                startUtc: data.startUtc,
                includePayoutDetails: data.includePayoutDetails,
            };
            await updateScheduledExport(requestObject);
            await invalidate('payoutReport', 'getScheduleExport', { exportScheduleId });
            refetchTableData();
        },
        resolver: createZodFormResolver(combinedSchema),
        defaultValues: scheduledExport.data,
        mode: 'onBlur',
    });

    const combinedFormDefinition: SchemaFormDefinition<typeof combinedSchema> = {
        fields: {
            ...exportConfigFormDefinition.fields,
            ...getScheduleConfigFormDefinition(exportTargetsOptions, true).fields,
        },
        renderFields: enablePayoutDetailsInPayoutExport
            ? undefined
            : [
                  'name',
                  'reportInterval',
                  'format',
                  'frequency',
                  'startUtc',
                  'exportTargetId',
                  'isEnabled',
                  'notifyCreatorAboutExport',
              ],
    };

    return renderFormContent(
        <>
            <SchemaFormFields schema={combinedSchema} formDefinition={combinedFormDefinition} />
        </>
    );
};
